import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
} from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import { useTranslation } from "react-i18next";
import StatusDropdown, {
  getAttendanceStatusDotColor,
  getAttendanceStatusPinMarkerIcon,
} from "../../../../components/StatusDropdown";
import CustomButton from "../../../../components/CustomButton";
import maparrow from "../../../../assets/maparrow.svg";
import CloseIcon from "@mui/icons-material/Close";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import {
  convertUTCToLocalDateTime,
  formatDurationMinutes,
  hoursDecimalToMinutes,
  parseDurationToMinutes,
} from "../../../../components/dateTimeUtils";
import {
  getViewerDisplayTimezone,
  formatScheduleBoundaryForViewer,
  isUtcLikeTimezone,
} from "../../../../components/attendanceScheduleDisplay";
const defaultCenter = { lat: 33.6844, lng: 73.0479 };
const defaultZoom = 13;
const DAY_STATUS_SX = {
  BREAK: { bg: "#EAE6FF", color: "#6554C0", border: "#6554C033" },
  ON_BREAK: { bg: "#EAE6FF", color: "#6554C0", border: "#6554C033" },
  default: { bg: "#E3FCEF", color: "#1F845A", border: "#1F845A33" },
};

function deriveScheduleMatchValid(scheduleStatus) {
  if (!scheduleStatus || scheduleStatus.hasSchedule === false) return null;
  const c = scheduleStatus.classification || "";
  if (c === "NOT_APPLICABLE") return true;
  if (scheduleStatus.isOnTime === true) return true;
  if (scheduleStatus.isEarly === true || scheduleStatus.isLate === true) return false;
  if (c.includes("EARLY") || c.includes("LATE")) return false;
  if (c.includes("ON_TIME")) return true;
  return null;
}

/**
 * Matches GET /attendance/monthly daily `break_time_minutes`: pair the i-th BREAK_START
 * with the i-th BREAK_END in API list order (same as monthly SQL `ORDER BY occurred_at DESC`
 * then filter — not FIFO after a full chronological sort, which powers `takenBreakMinutes`).
 * Uses whole minutes truncated toward zero (same as Moment `diff(..., "minutes")`), not
 * rounded — so e.g. ~3.5m on the clock shows as 3 min, not 4 (4 is policy allowance elsewhere).
 */
function computeBreakMinutesLikeMonthlySummary(records) {
  if (!Array.isArray(records) || records.length === 0) return null;
  const getAction = (p) => p?.actionType ?? p?.action_type ?? "";
  const getTime = (p) => p?.occurred_at ?? p?.occurredAt ?? p?.timeData?.occurredAt;
  const breakStarts = records.filter((p) => getAction(p) === "BREAK_START");
  const breakEnds = records.filter((p) => getAction(p) === "BREAK_END");
  let totalBreakMinutes = 0;
  const n = Math.min(breakStarts.length, breakEnds.length);
  for (let i = 0; i < n; i++) {
    const tStart = new Date(getTime(breakStarts[i])).getTime();
    const tEnd = new Date(getTime(breakEnds[i])).getTime();
    if (Number.isFinite(tStart) && Number.isFinite(tEnd) && tEnd > tStart) {
      totalBreakMinutes += Math.floor((tEnd - tStart) / 60000);
    }
  }
  return totalBreakMinutes;
}

function formatMinutesVsScheduleText(scheduleStatus, t) {
  const mins = scheduleStatus?.minutesFromSchedule;
  if (mins == null || scheduleStatus?.classification === "NOT_APPLICABLE") return null;
  const abs = Math.round(Math.abs(mins));
  const duration = formatDurationMinutes(abs) ?? `${abs} min`;
  const ref = scheduleStatus?.scheduleReference?.comparedWith;
  if (ref === "scheduled_start") {
    if (mins < 0) return t("attendance.todayPunchDetails.minutesBeforeScheduledStart", { duration });
    if (mins > 0) return t("attendance.todayPunchDetails.minutesAfterScheduledStart", { duration });
  }
  if (ref === "scheduled_end") {
    if (mins < 0) return t("attendance.todayPunchDetails.minutesBeforeScheduledEnd", { duration });
    if (mins > 0) return t("attendance.todayPunchDetails.minutesAfterScheduledEnd", { duration });
  }
  return duration;
}

function formatIssueKeyToTitleCase(key) {
  if (key == null || key === "") return "";
  return String(key)
    .trim()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function formatIssueLabel(key, t) {
  return t(`attendance.todayPunchDetails.issueLabels.${key}`, {
    defaultValue: formatIssueKeyToTitleCase(key),
  });
}

function formatIssuesFromIssues(issues, t) {
  if (!issues) return null;
  const fromAttendance = issues.attendance_issues;
  if (Array.isArray(fromAttendance) && fromAttendance.length > 0) {
    const labels = fromAttendance.map(
      (issue) => issue.label || (issue.type ? formatIssueLabel(issue.type, t) : null)
    );
    const unique = [...new Set(labels.filter(Boolean))];
    if (unique.length) return unique.join(", ");
  }
  const list = issues.issue_list;
  if (!list?.length) return null;
  return list.map((key) => formatIssueLabel(key, t)).join(", ");
}

const SectionHeader = ({ label }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 18,
        fontWeight: 600,
        color: "#000000",
      }}
    >
      {label}
    </CustomText>
  </Box>
);

const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);

const InfoRow = ({ label, value }) => {
  const isElement = React.isValidElement(value);
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1.5,
        gap: 1,
      }}
    >
      <CustomText
        sx={{
          ...textStyles.h1,
          fontSize: 15,
          fontWeight: 400,
          color: "#5E5C5C",
          textAlign: "left",
          flexShrink: 0,
        }}
      >
        {label}
      </CustomText>
      <Box sx={{ textAlign: "right", minWidth: 0 }}>
        {isElement ? (
          value
        ) : (
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 15,
              fontWeight: 400,
              color: "#172B4D",
              textAlign: "right",
              wordBreak: "break-word",
            }}
          >
            {value === undefined || value === null || value === "" ? "--" : value}
          </CustomText>
        )}
      </Box>
    </Box>
  );
};

const EvidenceThumb = ({ src, alt }) => (
  <Box
    sx={{
      width: 112,
      height: 100,
      borderRadius: "10px",
      border: "1px solid #E4E6EF",
      backgroundColor: "#F8F9FB",
      overflow: "hidden",
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {src ? (
      <img
        component="img"
        src={src}
        alt={alt}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
        crossOrigin="anonymous"
      />
    ) : (
      <CustomText sx={{ ...textStyles.h1, fontSize: 14, color: "#6B778C" }}>--</CustomText>
    )}
  </Box>
);

const ValidationBadge = ({ isValid, t }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 92,
      px: 1.5,
      py: 0.4,
      borderRadius: "4px",
      border: `1px solid ${isValid ? "#1F845A55" : "#DE350B55"}`,
      backgroundColor: isValid ? "#E3FCEF" : "#FFEBE6",
      color: isValid ? "#1F845A" : "#AE2A19",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.4,
    }}
  >
    {isValid ? t("summaryDetails.valid") : t("summaryDetails.invalid")}
  </Box>
);

const DayStatusChip = ({ statusKey, label }) => {
  const sx = DAY_STATUS_SX[statusKey] || DAY_STATUS_SX.default;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1.5,
        py: 0.35,
        borderRadius: "20px",
        border: `1px solid ${sx.border}`,
        backgroundColor: sx.bg,
        color: sx.color,
        fontSize: 13,
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      {label}
    </Box>
  );
};

export default function AttendanceDetailsDrawer({ open, onClose, record, todayContext }) {
  const { t } = useTranslation();
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const previewMapRef = useRef(null);
  const fullMapRef = useRef(null);
  const scheduleDebugLoggedRef = useRef(null);

  const markerPosition = useMemo(() => {
    const lat = parseFloat(
      record?.evidence?.location?.map_point?.lat ??
        record?.evidence?.location?.gps_latitude ??
        record?.locationData?.gps?.coordinates?.lat ??
        record?.location?.latitude
    );
    const lng = parseFloat(
      record?.evidence?.location?.map_point?.lng ??
        record?.evidence?.location?.gps_longitude ??
        record?.locationData?.gps?.coordinates?.lng ??
        record?.location?.longitude
    );

    if (!isFinite(lat) || !isFinite(lng)) {
      return null;
    }

    return { lat, lng };
  }, [record]);

  const fitMapToMarker = useCallback((map) => {
    if (!map || !window.google) return;

    if (!markerPosition) {
      map.setCenter(defaultCenter);
      map.setZoom(defaultZoom);
      return;
    }

    map.setCenter(markerPosition);
    map.setZoom(defaultZoom);
  }, [markerPosition]);

  const getMarkerIcon = useCallback(
    (type, statusKey) => getAttendanceStatusPinMarkerIcon(statusKey, type),
    []
  );

  const displayTimezone = useMemo(() => {
    const explicit = todayContext?.viewerResolvedTimeZone;

    if (
      explicit != null &&
      String(explicit).trim() !== "" &&
      !isUtcLikeTimezone(explicit)
    ) {
      return String(explicit).trim();
    }

    return getViewerDisplayTimezone(todayContext);
  }, [todayContext]);

  useEffect(() => {
    if (!open) scheduleDebugLoggedRef.current = null;
  }, [open]);

  useEffect(() => {
    fitMapToMarker(previewMapRef.current);
    fitMapToMarker(fullMapRef.current);
  }, [fitMapToMarker, isFullMapOpen]);

  if (!record) return null;

  const formatActionType = (actionType) => {
    if (!actionType) return "--";
    return String(actionType)
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const mapAttendanceReviewStatus = (reviewStatus) => {
    if (!reviewStatus) return reviewStatus;
    const normalizedStatus = String(reviewStatus).trim().toLowerCase().replace(/\s+/g, "_");

    if (normalizedStatus === "pending") return "pendingPunch";
    if (["approved", "validated", "valid"].includes(normalizedStatus)) return "validatedPunch";
    if (["rejected", "invalid", "has_issue", "has_issues"].includes(normalizedStatus)) {
      return "invalidPunch";
    }

    return reviewStatus;
  };

  const actionType = record?.actionType || record?.action_type;

  const samePunchId = (a, b) =>
    a != null && b != null && String(a) === String(b);

  const punchLite = todayContext?.punches?.find(
    (p) =>
      samePunchId(p.punchId, record.punchId) ||
      samePunchId(p.punchId, record.id) ||
      samePunchId(p.id, record.punchId) ||
      samePunchId(p.id, record.id)
  );
  const selectedRawRecord =
    todayContext?.records?.find(
      (p) =>
        samePunchId(p.punchId, record?.punchId) ||
        samePunchId(p.punchId, record?.id) ||
        samePunchId(p.id, record?.punchId) ||
        samePunchId(p.id, record?.id)
    ) || record;
  const apiScheduleStatus =
  selectedRawRecord?.scheduleStatus ??
  selectedRawRecord?.schedule_status ??
  record?.scheduleStatus ??
  record?.schedule_status;
  const liteTiming = punchLite?.schedule_timing;
  // const scheduleStatus = (() => {


  //   const api = apiScheduleStatus;
  //   if (api) {
  //     if (String(api.classification || "").trim()) {
  //       return api;
  //     }
  //     if (liteTiming?.classification) {
  //       const ref = String(actionType || "").toUpperCase().includes("OUT")
  //         ? "scheduled_end"
  //         : "scheduled_start";
  //       return {
  //         ...api,
  //         hasSchedule: true,
  //         classification: liteTiming.classification,
  //         minutesFromSchedule:
  //           liteTiming.minutesFromSchedule ?? api.minutesFromSchedule,
  //         isEarly: liteTiming.isEarly ?? api.isEarly,
  //         isLate: liteTiming.isLate ?? api.isLate,
  //         isOnTime: liteTiming.isOnTime ?? api.isOnTime,
  //         scheduleReference: api.scheduleReference || {
  //           date: record?.timeData?.date ?? todayContext?.date,
  //           timezone:
  //             record?.timeData?.timezone ?? todayContext?.timezone ?? "UTC",
  //           scheduledStart: liteTiming.scheduleStart,
  //           scheduledEnd: liteTiming.scheduleEnd,
  //           graceMinutes: liteTiming.graceMinutes,
  //           comparedWith: ref,
  //         },
  //       };
  //     }
  //     return api;
  //   }
  //   if (liteTiming?.classification) {
  //     const ref = String(actionType || "").toUpperCase().includes("OUT")
  //       ? "scheduled_end"
  //       : "scheduled_start";
  //     return {
  //       hasSchedule: true,
  //       classification: liteTiming.classification,
  //       minutesFromSchedule: liteTiming.minutesFromSchedule,
  //       isEarly: liteTiming.isEarly,
  //       isLate: liteTiming.isLate,
  //       isOnTime: liteTiming.isOnTime,
  //       scheduleReference: {
  //         date: record?.timeData?.date ?? todayContext?.date,
  //         timezone: record?.timeData?.timezone ?? todayContext?.timezone ?? "UTC",
  //         scheduledStart: liteTiming.scheduleStart,
  //         scheduledEnd: liteTiming.scheduleEnd,
  //         graceMinutes: liteTiming.graceMinutes,
  //         comparedWith: ref,
  //       },
  //     };
  //   }
  //   return api || null;
  // })();


  const scheduleStatus = (() => {
    const api = apiScheduleStatus;
    const lite = punchLite?.schedule_timing;

    const ref = String(actionType || "").toUpperCase().includes("OUT")
      ? "scheduled_end"
      : "scheduled_start";

    const attendanceIssueFromClassification = (classification) => {
      if (classification === "LATE_CLOCK_IN") return "Late Clock In";
      if (classification === "EARLY_CLOCK_IN") return "Early Clock In";
      if (classification === "EARLY_CLOCK_OUT") return "Early Clock Out";
      if (classification === "LATE_CLOCK_OUT") return "Late Clock Out";
      return null;
    };

    const buildFromLite = (baseApi) => {
      if (!lite?.classification) return null;
      const classification = String(
        lite.classification ?? punchLite?.timing?.attendanceCategory ?? ""
      ).trim();

      return {
        ...(baseApi || {}),
        hasSchedule: true,
        classification,
        minutesFromSchedule: lite.minutesFromSchedule,
        isEarly: lite.isEarly,
        isLate: lite.isLate,
        isOnTime: lite.isOnTime,
        attendanceIssue: attendanceIssueFromClassification(classification),
        scheduleReference: {
          ...(baseApi?.scheduleReference || {}),
          date: record?.timeData?.date ?? todayContext?.date,
          timezone:
            record?.timeData?.timezone ?? todayContext?.timezone ?? "UTC",
          scheduledStart: lite.scheduleStart,
          scheduledEnd: lite.scheduleEnd,
          scheduledTime:
            ref === "scheduled_end" ? lite.scheduleEnd : lite.scheduleStart,
          comparedWith: ref,
          graceMinutes: lite.graceMinutes,
        },
      };
    };

    // Trust per-punch API evaluation first (matches monthly summary / Postman).
    // `punches[].schedule_timing` can disagree (e.g. wrong reference date → bogus minutes).
    if (api && String(api.classification || "").trim()) {
      return api;
    }

    if (api && lite?.classification) {
      const classification = String(
        lite.classification ?? punchLite?.timing?.attendanceCategory ?? ""
      ).trim();
      return {
        ...api,
        hasSchedule: true,
        classification,
        minutesFromSchedule: lite.minutesFromSchedule ?? api.minutesFromSchedule,
        isEarly: lite.isEarly ?? api.isEarly,
        isLate: lite.isLate ?? api.isLate,
        isOnTime: lite.isOnTime ?? api.isOnTime,
        attendanceIssue: attendanceIssueFromClassification(classification),
        scheduleReference:
          api.scheduleReference ||
          {
            date: record?.timeData?.date ?? todayContext?.date,
            timezone:
              record?.timeData?.timezone ?? todayContext?.timezone ?? "UTC",
            scheduledStart: lite.scheduleStart,
            scheduledEnd: lite.scheduleEnd,
            scheduledTime:
              ref === "scheduled_end" ? lite.scheduleEnd : lite.scheduleStart,
            comparedWith: ref,
            graceMinutes: lite.graceMinutes,
          },
      };
    }

    const liteOnly = buildFromLite(api);
    if (liteOnly) return liteOnly;

    return api || null;
  })();

  if (open) {
    const api = apiScheduleStatus;
    const lite = punchLite?.schedule_timing;
    let branch = "none";
    if (api && String(api.classification || "").trim()) branch = "api_has_classification";
    else if (api && lite?.classification) branch = "merge_api_lite";
    else if (lite?.classification) branch = "lite_only";
    else if (api) branch = "api_only_no_class";

    const dbgKey = `${record?.punchId ?? record?.id ?? "?"}-${branch}`;
    if (scheduleDebugLoggedRef.current !== dbgKey) {
      scheduleDebugLoggedRef.current = dbgKey;
      console.log("[TodayPunch DetailsDrawer] scheduleStatus trace", {
      branch,
      recordKeys: { punchId: record?.punchId, id: record?.id },
      selectedRawHasScheduleStatus: !!(
        selectedRawRecord?.scheduleStatus || selectedRawRecord?.schedule_status
      ),
      apiFromRecord: {
        classification: api?.classification,
        minutesFromSchedule: api?.minutesFromSchedule,
        comparedWith: api?.scheduleReference?.comparedWith,
        hasSchedule: api?.hasSchedule,
      },
      punchLiteFound: !!punchLite,
      liteSchedule_timing: lite
        ? {
            classification: lite.classification,
            minutesFromSchedule: lite.minutesFromSchedule,
            isEarly: lite.isEarly,
            isLate: lite.isLate,
          }
        : null,
      resolvedScheduleStatus: scheduleStatus
        ? {
            classification: scheduleStatus.classification,
            minutesFromSchedule: scheduleStatus.minutesFromSchedule,
            comparedWith: scheduleStatus.scheduleReference?.comparedWith,
          }
        : null,
    });
    }
  }

  const scheduleMatchValid = deriveScheduleMatchValid(scheduleStatus);
  const occurredAt =
  selectedRawRecord?.occurred_at ??
  selectedRawRecord?.occurredAt ??
  record?.occurred_at ??
  record?.occurredAt;
  const settings = todayContext?.attendanceSettings;
  const schedRoot = todayContext?.schedule || todayContext?.details?.schedule;
  const dailySummary = todayContext?.dailySummary;
  const lastPunch = dailySummary?.lastPunch;
  const lastStatusKey = todayContext?.lastStatus || todayContext?.workerStatus;
  // When `scheduleStatus` is present (especially from `records[].scheduleStatus`), do not
  // let `punches[].schedule_timing` override labels — that caused late vs early mismatches.
  const classification = String(
    scheduleStatus != null
      ? (scheduleStatus.classification != null && scheduleStatus.classification !== ""
          ? scheduleStatus.classification
          : liteTiming?.classification ?? punchLite?.timing?.attendanceCategory ?? "")
      : liteTiming?.classification ?? punchLite?.timing?.attendanceCategory ?? ""
  ).trim();

  const punchTimeDisplay = (() => {
    const iso =
      occurredAt ||
      record?.occurred_at ||
      record?.occurredAt ||
      record?.deviceTimestamp ||
      record?.device_timestamp;
    const v = convertUTCToLocalDateTime(iso, {
      timezone: displayTimezone,
    });
    return v === "—" || v === "\u2014" ? "--" : v;
  })();

  const locationRowKey =
    actionType === "CLOCK_IN"
      ? "clockInLocation"
      : actionType === "CLOCK_OUT"
        ? "clockOutLocation"
        : actionType === "BREAK_START"
          ? "breakStartLocation"
          : actionType === "BREAK_END"
            ? "breakEndLocation"
            : "punchLocation";

  const isBreakPunch = actionType === "BREAK_START" || actionType === "BREAK_END";

  const showScheduleMatch = !isBreakPunch;

  const showClockScheduleRows =
    (actionType === "CLOCK_IN" || actionType === "CLOCK_OUT") &&
    classification &&
    classification !== "NOT_APPLICABLE" &&
    !String(classification).includes("ON_TIME") &&
    scheduleStatus?.scheduleReference;

  // const scheduledWindowText = (() => {
  //   const ref = scheduleStatus?.scheduleReference;
  //   const sched = settings?.schedule;
  //   const startH =
  //     ref?.scheduledStart ||
  //     ref?.scheduled_start ||
  //     punchLite?.schedule_timing?.scheduleStart ||
  //     sched?.startTime ||
  //     todayContext?.scheduledStartTime ||
  //     todayContext?.details?.scheduledStart;
  //   const endH =
  //     ref?.scheduledEnd ||
  //     ref?.scheduled_end ||
  //     punchLite?.schedule_timing?.scheduleEnd ||
  //     sched?.endTime ||
  //     todayContext?.scheduledEndTime ||
  //     todayContext?.details?.scheduledEnd;
  //   const grace =
  //     ref?.graceMinutes ??
  //     ref?.grace_minutes ??
  //     punchLite?.schedule_timing?.graceMinutes ??
  //     sched?.graceMinutes;
  //   if (!startH || !endH) return null;
  //   const scheduleDateRaw =
  //     ref?.date ||
  //     record?.date ||
  //     record?.timeData?.date ||
  //     todayContext?.date ||
  //     (typeof occurredAt === "string" && occurredAt.length >= 10 ? occurredAt.slice(0, 10) : null);
  //   if (!scheduleDateRaw) return null;
  //   const srcTz = String(ref?.timezone ?? todayContext?.timezone ?? "UTC").trim() || "UTC";
  //   let startStr =
  //     formatScheduleBoundaryForViewer(scheduleDateRaw, startH, srcTz, displayTimezone) || "--";
  //   let endStr =
  //     formatScheduleBoundaryForViewer(scheduleDateRaw, endH, srcTz, displayTimezone) || "--";
  //   if (startStr === "—" || startStr === "\u2014") startStr = "--";
  //   if (endStr === "—" || endStr === "\u2014") endStr = "--";
  //   return t("attendance.todayPunchDetails.scheduledWindow", {
  //     start: startStr,
  //     end: endStr,
  //     grace: grace ?? "--",
  //   });
  // })();




  const scheduledWindowText = (() => {
    const ref = scheduleStatus?.scheduleReference;
    const sched = settings?.schedule;
  
    const startH =
      ref?.scheduledStart ||
      ref?.scheduled_start ||
      punchLite?.schedule_timing?.scheduleStart ||
      sched?.startTime ||
      todayContext?.scheduledStartTime ||
      todayContext?.details?.scheduledStart;
  
    const endH =
      ref?.scheduledEnd ||
      ref?.scheduled_end ||
      punchLite?.schedule_timing?.scheduleEnd ||
      sched?.endTime ||
      todayContext?.scheduledEndTime ||
      todayContext?.details?.scheduledEnd;
  
    const grace =
      ref?.graceMinutes ??
      ref?.grace_minutes ??
      punchLite?.schedule_timing?.graceMinutes ??
      sched?.graceMinutes;
  
    if (!startH || !endH) return null;
  
    const scheduleDateRaw =
      ref?.date ||
      record?.timeData?.date ||
      todayContext?.date ||
      (typeof occurredAt === "string" && occurredAt.length >= 10
        ? occurredAt.slice(0, 10)
        : null);
  
    if (!scheduleDateRaw) return null;
  
    const ymdMatch = String(scheduleDateRaw).match(/^(\d{4}-\d{2}-\d{2})/);
    const ymd = ymdMatch ? ymdMatch[1] : null;
  
    if (!ymd) return null;
  
    const normalizeHms = (time) => {
      const parts = String(time).split(":");
      const h = String(parts[0] || "00").padStart(2, "0");
      const m = String(parts[1] || "00").padStart(2, "0");
      const s = String(parts[2] || "00").padStart(2, "0");
      return `${h}:${m}:${s}`;
    };
  
    const startStr = convertUTCToLocalDateTime(
      `${ymd}T${normalizeHms(startH)}Z`,
      {
        timezone: displayTimezone,
        format: "h:mm a",
      }
    );
  
    const endStr = convertUTCToLocalDateTime(
      `${ymd}T${normalizeHms(endH)}Z`,
      {
        timezone: displayTimezone,
        format: "h:mm a",
      }
    );
  
    return t("attendance.todayPunchDetails.scheduledWindow", {
      start: startStr === "—" ? "--" : startStr,
      end: endStr === "—" ? "--" : endStr,
      grace: grace ?? "--",
    });
  })();


  const minutesVsText = formatMinutesVsScheduleText(scheduleStatus, t);
  const punchIssues =
    punchLite?.issues ?? selectedRawRecord?.issues ?? record?.issues ?? null;
  const issuesFromValidation = record?.validation?.validationIssues?.length
    ? record.validation.validationIssues.map((v) => v.message).filter(Boolean).join(", ")
    : null;
  const issuesLine = formatIssuesFromIssues(punchIssues, t) || issuesFromValidation;
  const issueCountDisplay =
    punchIssues?.issue_count ??
    record?.issue_count ??
    record?.validation?.validationIssues?.length ??
    null;

  const dayIssueSummary =
    todayContext?.issue_summary ?? todayContext?.dailySummary?.issue_summary ?? null;
  const dayTotalIssueCount =
    todayContext?.total_issue_count ??
    todayContext?.dailySummary?.total_issue_count ??
    dayIssueSummary?.total_issue_count ??
    null;
  const dayIssuesDetailsStr = formatIssuesFromIssues(dayIssueSummary, t);

  const summaryLastActivityStatus = (() => {
    const c =
      lastPunch?.scheduleStatus?.classification ??
      lastPunch?.schedule_status?.classification;
    if (c != null && String(c).trim() !== "") return String(c).trim();
    return lastPunch?.actionType ?? lastPunch?.action_type ?? null;
  })();

  const summaryLocationText =
    lastPunch?.locationData?.textDescription ||
    todayContext?.dailySummary?.lastPunch?.locationData?.textDescription ||
    "--";

  const scheduledWorkingMinutes =
    schedRoot?.scheduledWorkingMinutes ??
    hoursDecimalToMinutes(schedRoot?.scheduledWorkingHours) ??
    hoursDecimalToMinutes(schedRoot?.dailyHours ?? settings?.schedule?.dailyHours);
  const scheduledHoursStr = formatDurationMinutes(scheduledWorkingMinutes) ?? "--";

  const completedWorkingMinutes =
    schedRoot?.completedWorkingMinutes ??
    dailySummary?.workingHours?.minutes ??
    parseDurationToMinutes(dailySummary?.workingHours?.total) ??
    hoursDecimalToMinutes(dailySummary?.workingHours?.hours) ??
    hoursDecimalToMinutes(todayContext?.working_hours) ??
    parseDurationToMinutes(todayContext?.gross_working_minutes);
  const workingHoursStr = formatDurationMinutes(completedWorkingMinutes) ?? "--";
  const breakAllowMin =
    settings?.schedule?.breakPolicy?.unpaid ??
    schedRoot?.breakPolicy?.unpaid ??
    schedRoot?.breakDurationMinutes;
  const breakAllowStr = formatDurationMinutes(breakAllowMin) ?? "--";
  // Prefer monthly-aligned math from `records` so "Break time taken" matches monthly summary;
  // `break_time_minutes` / `schedule.takenBreakMinutes` uses FIFO+chronological sort and can disagree.
  const breakMinutesFromRecords = computeBreakMinutesLikeMonthlySummary(todayContext?.records);
  const breakMinutesValue =
    breakMinutesFromRecords != null ? breakMinutesFromRecords : todayContext?.break_time_minutes;
  // Whole minutes only (same spirit as Moment diff); never conflate with scheduled allowance (`breakAllowMin`).
  const breakTakenDay = formatDurationMinutes(breakMinutesValue) ?? "--";

  const dayStatusLabel = lastStatusKey
    ? t(`attendance.todayPunchDetails.dayStatus.${lastStatusKey}`, {
        defaultValue: formatActionType(lastStatusKey),
      })
    : "--";

  const headerDate = (() => {
    const ymd =
      todayContext?.date ||
      (typeof occurredAt === "string" && occurredAt.length >= 10 ? occurredAt.slice(0, 10) : null);
    if (!ymd) return null;
    const v = convertUTCToLocalDateTime(`${String(ymd).trim()}T12:00:00Z`, {
      timezone: displayTimezone,
      formatOptions: {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      },
    });
    return v === "—" || v === "\u2014" ? null : v;
  })();

  const validationData = record?.validation || record?.evidence?.validation || {};
  const locationEvidence = record?.locationData?.gps || record?.evidence?.location || {};
  const selfieUrl =
    record?.faceScanData?.evidence?.capturedImageUrl ||
    record?.evidencePhoto ||
    punchLite?.evidencePhoto ||
    record?.evidence?.selfie_url ||
    record?.selfie_url ||
    null;
  const evidenceLocationText =
    record?.locationData?.textDescription ||
    record?.location?.locationText ||
    [locationEvidence?.latitude, locationEvidence?.longitude].filter(Boolean).join(", ");
  const actionLabel = t(`attendance.actionType.${actionType}`, {
    defaultValue: formatActionType(actionType),
  });

  const classificationLabel = classification
    ? t(`attendance.issueClassification.${classification}`, {
        defaultValue: formatActionType(classification),
      })
    : null;

  const attendanceIssueLabel =
    classification && classification !== "NOT_APPLICABLE" ? classificationLabel : null;

  const punchStatusKey = classification || actionType;
  const punchHeaderLabel =
    classification && classification !== "NOT_APPLICABLE"
      ? classificationLabel
      : actionLabel;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 550 }, borderRadius: 0 },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          py: 1.5,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <Box>
         
            {headerDate ? (
              <CustomText     sx={{
                fontSize: 20,
                fontWeight: 600,
                color: "#2C384C",
                fontFamily: "Poppins, sans-serif",
              }}
              >
                {headerDate}
              </CustomText>
            ) : null}
          </Box>
        </Box>
      </Box>
      <Divider />

      <Box sx={{ px: 2, py: 2, overflowY: "auto" }}>
        {todayContext?.worker ? (
          <>
            <InfoBox >
              <InfoRow
                label={t("attendanceDrawer.workerName")}
                value={todayContext.worker.fullName || "--"}
              />
              <InfoRow
                label={t("attendanceDrawer.department")}
                value={todayContext.worker.department || "--"}
              />
              <InfoRow
                label={t("attendance.todayPunchDetails.lastActivity")}
                value={
                  summaryLastActivityStatus ? (
                    <Box sx={{ pointerEvents: "none", display: "flex", justifyContent: "flex-end" }}>
                      <StatusDropdown currentStatus={summaryLastActivityStatus} />
                    </Box>
                  ) : (
                    "--"
                  )
                }
              />
              <InfoRow label={t("summaryDetails.location")} value={summaryLocationText} />
              <InfoRow label={t("attendance.todayPunchDetails.scheduledWorkingHours")} value={scheduledHoursStr} />
              <InfoRow label={t("attendanceDrawer.workingHours")} value={workingHoursStr} />
              <InfoRow label={t("attendance.todayPunchDetails.scheduledBreakAllowance")} value={breakAllowStr} />
              <InfoRow label={t("attendance.todayPunchDetails.breakTimeTaken")} value={breakTakenDay} />
              {dayTotalIssueCount != null && Number(dayTotalIssueCount) > 0 ? (
                <InfoRow label={t("totalIssuesCount")} value={String(dayTotalIssueCount)} />
              ) : null}
              {dayIssuesDetailsStr ? (
                <InfoRow label={t("issuesDetails")} value={dayIssuesDetailsStr} />
              ) : (
                <InfoRow label={t("attendanceDrawer.details")} value="--" />
              )}
            </InfoBox>
          </>
        ) : null}

        <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: 1,
              backgroundColor: getAttendanceStatusDotColor(
                punchStatusKey,
                actionType
              ),
              flexShrink: 0,
            }}
          />
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#000000",
            }}
          >
            {punchHeaderLabel}
          </CustomText>
        </Box>
        <InfoBox>
          <InfoRow
            label={t("summaryDetails.status")}
            value={
              <Box sx={{ pointerEvents: "none", display: "flex", justifyContent: "flex-end" }}>
                <StatusDropdown currentStatus={punchStatusKey} />
              </Box>
            }
          />
          <InfoRow label={t("summaryDetails.punchTime")} value={punchTimeDisplay} />
          <InfoRow
            label={t(`attendance.todayPunchDetails.${locationRowKey}`)}
            value={evidenceLocationText || "--"}
          />

          <InfoRow
            label={t("attendance.todayPunchDetails.scheduleMatch")}
            value={
              !showScheduleMatch || scheduleMatchValid === null ? (
                "--"
              ) : (
                <ValidationBadge isValid={scheduleMatchValid} t={t} />
              )
            }
          />

          {showClockScheduleRows && attendanceIssueLabel ? (
            <InfoRow label={t("summaryDetails.attendanceIssue")} value={attendanceIssueLabel} />
          ) : null}
          {showClockScheduleRows && minutesVsText ? (
            <InfoRow label={t("attendance.todayPunchDetails.minutesVsSchedule")} value={minutesVsText} />
          ) : null}
          {showClockScheduleRows && scheduledWindowText ? (
            <InfoRow
              label={t("attendance.todayPunchDetails.scheduledWindowLabel")}
              value={scheduledWindowText}
            />
          ) : null}
          {issueCountDisplay != null && Number(issueCountDisplay) > 0 ? (
            <InfoRow
              label={t("attendance.todayPunchDetails.issueCount")}
              value={String(issueCountDisplay)}
            />
          ) : null}
          {issuesLine ? <InfoRow label={t("issuesDetails")} value={issuesLine} /> : null}

          <InfoRow
            label={t("attendanceDrawer.selfie")}
            value={<EvidenceThumb src={selfieUrl} alt={t("attendanceDrawer.selfie")} />}
          />
          <InfoRow
            label={t("summaryDetails.faceMatched")}
            value={
              <ValidationBadge
                isValid={
                  record?.face_matched === true ||
                  record?.faceScanData?.verification?.matched === true ||
                  validationData?.face_matched === true ||
                  punchLite?.validation?.faceMatched === true
                }
                t={t}
              />
            }
          />
          <InfoRow
            label={t("summaryDetails.locationValidated")}
            value={
              <ValidationBadge
                isValid={
                  validationData?.location_validated === true ||
                  record?.location_validated === true ||
                  record?.locationData?.validation?.isValidated === true ||
                  punchLite?.validation?.locationMatched === true
                }
                t={t}
              />
            }
          />
          <InfoRow
            label={t("summaryDetails.reviewStatus")}
            value={
              <Box sx={{ pointerEvents: "none" }}>
                <StatusDropdown
                  currentStatus={mapAttendanceReviewStatus(
                    validationData?.reviewStatus ||
                      validationData?.review_status ||
                      record?.validation?.reviewStatus ||
                      record?.validation?.review_status ||
                      punchLite?.validation?.reviewStatus ||
                      "--"
                  )}
                />
              </Box>
            }
          />
          <InfoRow
            label={t("summaryDetails.reviewNote")}
            value={
              validationData?.reviewNote ||
              validationData?.review_note ||
              record?.validation?.reviewNote ||
              "--"
            }
          />
        </InfoBox>

        <SectionHeader label={t("attendanceDrawer.mapView")} />
        <InfoBox>
          <Box sx={{ position: "relative" }}>
            <GoogleMap
              mapContainerStyle={{ height: "300px", width: "100%" }}
              center={markerPosition || defaultCenter}
              zoom={defaultZoom}
              options={{ streetViewControl: false, mapTypeControl: false }}
              onLoad={(map) => {
                previewMapRef.current = map;
                fitMapToMarker(map);
              }}
            >
              {markerPosition && (
                <Marker
                  position={markerPosition}
                  icon={getMarkerIcon(actionType, punchStatusKey)}
                  onClick={() => setIsInfoOpen(true)}
                >
                  {isInfoOpen && (
                    <InfoWindow onCloseClick={() => setIsInfoOpen(false)}>
                      <div>{actionLabel}</div>
                    </InfoWindow>
                  )}
                </Marker>
              )}
            </GoogleMap>
            <CustomButton
              onClick={() => setIsFullMapOpen(true)}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                backgroundColor: "#006EC2",
                color: "white",
                fontSize: "12px",
                padding: "4px 10px",
                borderRadius: "6px",
                zIndex: 1000,
              }}
              label={
                <>
                  {t("dashboard.viewFullMap")}
                  <Box component="img" src={maparrow} alt="view icon" sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }} />
                </>
              }
            />
          </Box>
        </InfoBox>
      </Box>

      {isFullMapOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            backgroundColor: "white",
          }}
        >
          <IconButton
            onClick={() => setIsFullMapOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              zIndex: 2100,
              backgroundColor: "white",
              "&:hover": { backgroundColor: "white" },
            }}
          >
            <CloseIcon sx={{ color: "red" }} />
          </IconButton>

          <GoogleMap
            mapContainerStyle={{ height: "100%", width: "100%" }}
            center={markerPosition || defaultCenter}
            zoom={defaultZoom}
            options={{ streetViewControl: false, mapTypeControl: false }}
            onLoad={(map) => {
              fullMapRef.current = map;
              fitMapToMarker(map);
            }}
          >
            {markerPosition && (
              <Marker
                position={markerPosition}
                icon={getMarkerIcon(actionType, punchStatusKey)}
                onClick={() => setIsInfoOpen(true)}
              >
                {isInfoOpen && (
                  <InfoWindow onCloseClick={() => setIsInfoOpen(false)}>
                    <div>{actionLabel}</div>
                  </InfoWindow>
                )}
              </Marker>
            )}
          </GoogleMap>
        </Box>
      )}
    </Drawer>
  );
}
